import type { UseFetchOptions } from 'nuxt/app'

export const getStrapiKitsListPageUrl = ({
  slug,
  locale,
}: {
  slug?: string
  locale?: string
}) => {
  const eventListPageURLQuery = new URLSearchParams()

  eventListPageURLQuery.set('populate[showcase][populate][0]', 'first.image')
  eventListPageURLQuery.set('populate[showcase][populate][1]', 'second.image')
  eventListPageURLQuery.set('populate[showcase][populate][2]', 'third.image')
  eventListPageURLQuery.set('populate[horizontalCardList][populate]', 'image')

  // Optimize localizations - only fields needed for hreflang links and locale matching
  eventListPageURLQuery.set('populate[localizations][fields][0]', 'id')
  eventListPageURLQuery.set('populate[localizations][fields][1]', 'locale')
  eventListPageURLQuery.set('populate[localizations][fields][2]', 'slug')

  // Optimize SEO - use wildcard for seo (as suggested) to avoid field name issues
  eventListPageURLQuery.set('populate[seo][populate]', '*')

  if (slug) {
    eventListPageURLQuery.set('filters[slug][$eq]', slug)
  }

  if (locale) {
    eventListPageURLQuery.set('locale', locale)
  }

  return 'kits-list-pages?' + eventListPageURLQuery.toString()
}

export const getKitsSeoData = (data: any) => {
  const head = {
    title: data?.seo?.metaTitle || data?.title || '',
  }

  const meta = {
    title: data?.seo?.metaTitle || data?.title || '',
    description: data?.seo?.metaDescription || '',
    keywords: data?.seo?.metaKeywords || '',
    'og:title': data?.seo?.metaTitle || data?.title || '',
    'og:description': data?.seo?.metaDescription || '',
    'og:keywords': data?.seo?.metaKeywords || '',
    'og:image': data?.seo?.metaImage?.formats?.medium?.url || '',
  }

  return computed(() => ({
    head,
    meta,
    shouldBeIndexed: true,
    allowedParams: ['title', 'description', 'keywords', 'image'],
  }))
}

export function useStrapiKitsListPageWithNoLocaleFilter({
  slug,
  options = {},
}: {
  slug: string
  options?: UseFetchOptions<any, any>
}) {
  const { locales } = useI18n()

  // Fetch for each locale and combine results
  return useAsyncData(
    `kits-list-pages-${slug}`,
    async () => {
      const fetchPromises = locales.value.map((locale) => {
        const kitsUrl = getStrapiKitsListPageUrl({
          slug: slug + '_' + locale.code,
          locale: locale.code,
        })

        return useStrapi<any>({
          urlSuffix: kitsUrl,
          options,
        })
      })

      const results = await Promise.all(fetchPromises)
      const allPages = results.flatMap((result) => result.data.value || [])
      const uniquePages = new Map<string, any>()

      // First pass: add all pages to the map and set type to 'kits'
      allPages.forEach((page) => {
        page.type = 'kits' // Explicitly set type for kits pages
        uniquePages.set(page.id.toString(), page)
      })

      // Second pass: merge localizations
      allPages.forEach((page) => {
        const localizations = strapiEventListPageGetters.getLocalizations(page)
        if (localizations) {
          localizations.forEach((localization) => {
            localization.type = 'kits' // Also set type for localizations
            if (!uniquePages.has(localization.id.toString())) {
              uniquePages.set(localization.id.toString(), localization)
            }
          })
        }
      })

      const finalResult = Array.from(uniquePages.values())
      return finalResult
    },
    {
      server: true,
      lazy: false,
    }
  )
}
